import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  AlertTriangle,
  Camera,
  CheckCircle2,
  Circle,
  ClipboardList,
  Download,
  FileSignature,
  IdCard,
  Loader2,
  Pencil,
  RotateCcw,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Upload,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { fullName, initials } from "@/components/EmployeeFormSheet";
import { openProtectedFile } from "@/components/employee/tabHelpers";

/* -------------------------------------------------------------------------- */
/* Configuration                                                              */
/* -------------------------------------------------------------------------- */

const DATA_CONFIRMATION_DEF = {
  key: "data_confirmation",
  title: "Adatok megerősítése",
  short: "Adatlap",
  icon: ClipboardList,
  accent: "indigo",
  docType: "data_confirmation",
  description:
    "Generálj egy nyilatkozatot a megadott adatokról — a dolgozó kinyomtatja, aláírja és a saját kezű aláírásával hitelesíti, hogy az adatai a valóságnak megfelelnek.",
};

const CONTRACT_DEFS = [
  {
    key: "nda",
    title: "Titoktartási szerződés",
    short: "NDA",
    icon: ShieldCheck,
    accent: "indigo",
    docType: "nda",
    description:
      "Bizalmas információk védelméről szóló megállapodás — minden új belépő aláírja.",
  },
  {
    key: "employment",
    title: "Munkaszerződés",
    short: "Munkaszerződés",
    icon: FileSignature,
    accent: "emerald",
    docType: "employment_contract",
    description:
      "A foglalkoztatás típusa szerinti sablonból generálódik (Teljes vagy Részmunkaidős).",
  },
  {
    key: "gdpr",
    title: "Adatkezelési tájékoztató",
    short: "GDPR",
    icon: ScrollText,
    accent: "amber",
    docType: "gdpr",
    description:
      "GDPR — személyes adatkezelési tájékoztató, törvényi kötelezettség.",
  },
];

// EFO és Diákszövetkezetes dolgozók NEM kapnak munkaszerződést tőlünk.
const NO_EMPLOYMENT_CONTRACT_TYPES = new Set(["efo", "student_coop"]);

function contractDefsForEmployee(employee) {
  const et = employee?.employment_type;
  if (NO_EMPLOYMENT_CONTRACT_TYPES.has(et)) {
    return CONTRACT_DEFS.filter((d) => d.key !== "employment");
  }
  return CONTRACT_DEFS;
}

const ID_CARD_DEFS = [
  { type: "taj", title: "TAJ kártya", hint: "Társadalombiztosítási kártya" },
  { type: "tax", title: "Adókártya", hint: "Adóazonosító jel kártya" },
  { type: "address", title: "Lakcímkártya", hint: "Hivatalos lakcímkártya" },
  { type: "id_front", title: "SZIG eleje", hint: "Személyazonosító — első oldal" },
  { type: "id_back", title: "SZIG hátulja", hint: "Személyazonosító — hátoldal" },
];

const ACCENT = {
  indigo: {
    grad: "from-indigo-50 via-white to-indigo-50/30 dark:from-indigo-500/10 dark:via-background dark:to-indigo-500/[0.04]",
    iconBg: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-300",
    ring: "ring-indigo-200/60 dark:ring-indigo-500/20",
    button: "bg-indigo-600 text-white hover:bg-indigo-700",
  },
  emerald: {
    grad: "from-emerald-50 via-white to-emerald-50/30 dark:from-emerald-500/10 dark:via-background dark:to-emerald-500/[0.04]",
    iconBg: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300",
    ring: "ring-emerald-200/60 dark:ring-emerald-500/20",
    button: "bg-emerald-600 text-white hover:bg-emerald-700",
  },
  amber: {
    grad: "from-amber-50 via-white to-amber-50/30 dark:from-amber-500/10 dark:via-background dark:to-amber-500/[0.04]",
    iconBg: "bg-amber-500/10 text-amber-600 dark:text-amber-300",
    ring: "ring-amber-200/60 dark:ring-amber-500/20",
    button: "bg-amber-600 text-white hover:bg-amber-700",
  },
  slate: {
    grad: "from-slate-50 via-white to-slate-50/30 dark:from-slate-500/10 dark:via-background dark:to-slate-500/[0.04]",
    iconBg: "bg-slate-500/10 text-slate-700 dark:text-slate-300",
    ring: "ring-slate-200/60 dark:ring-slate-500/20",
    button: "bg-foreground text-background hover:bg-foreground/90",
  },
};

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function latestDoc(documents, predicate) {
  return [...documents]
    .filter(predicate)
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))[0];
}

function contractDocStatus(doc) {
  if (!doc) return "missing";
  if (doc.file2_url) return "signed";
  if (doc.file1_url) return "awaiting";
  return "missing";
}

export function isOnboardingDone({ completeness, documents, employee }) {
  if (!completeness || !completeness.complete) return false;
  // Adatlap (Adatok megerősítése) — generálni + aláírtan visszatölteni kell
  const confirmDoc = latestDoc(
    documents,
    (d) => d.doc_type === DATA_CONFIRMATION_DEF.docType && d.generated_from_template,
  );
  if (contractDocStatus(confirmDoc) !== "signed") return false;
  for (const def of contractDefsForEmployee(employee)) {
    const doc = latestDoc(
      documents,
      (d) => d.doc_type === def.docType && d.generated_from_template,
    );
    if (contractDocStatus(doc) !== "signed") return false;
  }
  for (const c of ID_CARD_DEFS) {
    const doc = latestDoc(documents, (d) => d.doc_type === c.type);
    if (!doc?.file1_url) return false;
  }
  return true;
}

/* -------------------------------------------------------------------------- */
/* Main onboarding shell                                                      */
/* -------------------------------------------------------------------------- */

export function EmployeeOnboarding({
  employee,
  documents,
  completeness,
  onChanged,
  onEditEmployee,
}) {
  const [templates, setTemplates] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [generating, setGenerating] = useState("");
  const [signing, setSigning] = useState(null);

  const refreshTemplates = async () => {
    try {
      const { data } = await api.get("/contract-templates");
      setTemplates(data);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    refreshTemplates();
  }, []);

  const tplByKind = useMemo(() => {
    const m = {};
    for (const t of templates) m[t.kind] = t;
    return m;
  }, [templates]);

  const employmentKind =
    employee?.employment_type === "part_time" ? "employment_part" : "employment_full";

  // STEP 1 — data filling
  const dataStepDone = !!completeness?.complete;
  const missing = completeness?.missing || [];

  // STEP 2 — data confirmation (auto-generated from employee data, no template)
  const confirmDoc = latestDoc(
    documents,
    (d) =>
      d.doc_type === DATA_CONFIRMATION_DEF.docType && d.generated_from_template,
  );
  const confirmStatus = contractDocStatus(confirmDoc);

  // STEPS 3-5 — contracts (EFO/Diákszövetkezet esetén a munkaszerződés ki van zárva)
  const visibleContractDefs = contractDefsForEmployee(employee);
  const contractCards = visibleContractDefs.map((def) => {
    const tplKind = def.key === "employment" ? employmentKind : def.key;
    const hasTemplate = !!tplByKind[tplKind]?.template;
    const doc = latestDoc(
      documents,
      (d) => d.doc_type === def.docType && d.generated_from_template,
    );
    const status = contractDocStatus(doc);
    return { def, hasTemplate, doc, status };
  });

  // STEPS 6-10 — ID photos
  const idCards = ID_CARD_DEFS.map((c) => {
    const doc = latestDoc(documents, (d) => d.doc_type === c.type);
    return { c, doc, hasPhoto: !!doc?.file1_url };
  });

  // Aggregate progress
  const totalSteps = 1 + 1 + contractCards.length + idCards.length; // 1 + 1 + 3 + 5 = 10
  const doneSteps =
    (dataStepDone ? 1 : 0) +
    (confirmStatus === "signed" ? 1 : 0) +
    contractCards.filter((x) => x.status === "signed").length +
    idCards.filter((x) => x.hasPhoto).length;
  const progressPct = Math.round((doneSteps / totalSteps) * 100);

  const generate = async (defKey) => {
    if (!dataStepDone) {
      toast.error("Előbb töltsd ki a dolgozó adatait (1. lépés).");
      return;
    }
    setGenerating(defKey);
    try {
      if (defKey === "data_confirmation") {
        await api.post(`/employees/${employee._id}/data-confirmation/generate`);
      } else {
        const kind = defKey === "employment" ? employmentKind : defKey;
        await api.post(`/employees/${employee._id}/contracts/generate`, { kind });
      }
      toast.success("Dokumentum legenerálva. Tölts vissza aláírt példányt.");
      onChanged?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        (typeof detail === "object" ? detail?.message : formatApiError(detail)) ||
          "Generálás sikertelen.",
      );
    } finally {
      setGenerating("");
    }
  };

  return (
    <div data-testid="employee-onboarding" className="space-y-6">
      {/* Top: back link only — minimal focus mode */}
      <Link
        to="/employees"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Munkavállalók
      </Link>

      {/* Hero card */}
      <section className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-[hsl(var(--sipx-elevated))] via-[hsl(var(--sipx-elevated))] to-indigo-50/40 p-8 shadow-[0_30px_80px_-40px_rgba(15,23,42,0.25)] dark:to-indigo-500/[0.06]">
        {/* dot grid */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.05] [background-image:radial-gradient(circle_at_1px_1px,_currentColor_1px,_transparent_0)] [background-size:18px_18px]"
          aria-hidden
        />
        {/* color blobs */}
        <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-indigo-300/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-16 h-72 w-72 rounded-full bg-emerald-300/15 blur-3xl" />

        <div className="relative flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-5">
            <div className="grid h-16 w-16 place-items-center rounded-2xl bg-foreground text-background text-base font-semibold tracking-tight">
              {initials(employee)}
            </div>
            <div>
              <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.24em] text-indigo-700/80 dark:text-indigo-300/80">
                <Sparkles className="h-3 w-3" />
                Onboarding folyamatban
              </div>
              <h1
                data-testid="employee-name"
                className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl"
              >
                {employee.titulus ? `${employee.titulus} ` : ""}
                {fullName(employee)}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {doneSteps} / {totalSteps} lépés kész — a profil többi része
                csak az onboarding lezárása után válik elérhetővé.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setTplOpen(true)}
            data-testid="manage-templates-button"
            className="self-start"
          >
            <Pencil className="h-3.5 w-3.5" />
            Sablonok kezelése
          </Button>
        </div>

        {/* Progress */}
        <div className="relative mt-6">
          <div className="h-2 overflow-hidden rounded-full bg-foreground/[0.08]">
            <div
              className="h-full rounded-full bg-gradient-to-r from-indigo-500 via-emerald-500 to-amber-500 transition-[width] duration-700"
              style={{ width: `${progressPct}%` }}
              data-testid="onboarding-progress"
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-[11.5px] tabular-nums text-muted-foreground">
            <span>
              <b className="text-foreground">{progressPct}%</b> teljes
            </span>
            <span>{totalSteps - doneSteps} hátralévő lépés</span>
          </div>
        </div>
      </section>

      {/* STEP 1: Adatok kitöltése */}
      <StepShell
        index={1}
        title="Alapadatok kitöltése"
        subtitle="Személyes és munkaviszonyi adatok hiánytalanul"
        done={dataStepDone}
        accent="slate"
        icon={ClipboardList}
        testId="step-data"
      >
        {dataStepDone ? (
          <p className="text-[13px] text-emerald-700 dark:text-emerald-300">
            Az adatok hiánytalanok — a többi lépés elérhetővé vált.
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-[13px] text-muted-foreground">
              {missing.length} kötelező mező vár kitöltésre. A{" "}
              <b className="text-foreground">Titulus</b> mező opcionális.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {missing.map((m) => (
                <span
                  key={m.field}
                  data-testid={`missing-chip-${m.field}`}
                  className="inline-flex items-center rounded-full border border-rose-300/60 bg-rose-50 px-2.5 py-0.5 text-[11.5px] font-medium text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200"
                >
                  {m.label}
                </span>
              ))}
            </div>
            <Button
              size="sm"
              onClick={onEditEmployee}
              data-testid="onboarding-edit-data"
              className="bg-foreground text-background hover:bg-foreground/90"
            >
              <Pencil className="h-3.5 w-3.5" />
              Adatok kitöltése most
            </Button>
          </div>
        )}
      </StepShell>

      {/* STEP 2: Adatok megerősítése (auto-generated, no template) */}
      {(() => {
        const Icon = DATA_CONFIRMATION_DEF.icon;
        const accent = ACCENT[DATA_CONFIRMATION_DEF.accent];
        const isSigned = confirmStatus === "signed";
        const isAwaiting = confirmStatus === "awaiting";
        const locked = !dataStepDone;
        return (
          <StepShell
            index={2}
            title={DATA_CONFIRMATION_DEF.title}
            subtitle={DATA_CONFIRMATION_DEF.description}
            done={isSigned}
            locked={locked}
            accent={DATA_CONFIRMATION_DEF.accent}
            icon={Icon}
            testId="step-data-confirmation"
          >
            <div className="flex flex-wrap items-center gap-2">
              <StatusChip status={confirmStatus} />
              <span className="text-[11px] text-muted-foreground">
                Sablon nem kell — a rendszer közvetlenül a megadott adatokból
                készít aláírható nyilatkozatot.
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {!confirmDoc && (
                <Button
                  size="sm"
                  disabled={locked || generating === "data_confirmation"}
                  onClick={() => generate("data_confirmation")}
                  className={accent.button}
                  data-testid="generate-data_confirmation"
                >
                  {generating === "data_confirmation" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5" />
                  )}
                  Generálás
                </Button>
              )}
              {confirmDoc && confirmDoc.file1_url && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    openProtectedFile(
                      `/employees/${employee._id}/documents/${confirmDoc._id}/file/file1`,
                      "application/pdf",
                      "adatlap",
                    )
                  }
                  data-testid="download-generated-data_confirmation"
                >
                  <Download className="h-3.5 w-3.5" />
                  Generált letöltése
                </Button>
              )}
              {confirmDoc && confirmStatus === "awaiting" && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={locked || generating === "data_confirmation"}
                  onClick={() => generate("data_confirmation")}
                  data-testid="regenerate-data_confirmation"
                >
                  {generating === "data_confirmation" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RotateCcw className="h-3.5 w-3.5" />
                  )}
                  Újragenerálás
                </Button>
              )}
              {confirmDoc && confirmDoc.file2_url && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    openProtectedFile(
                      `/employees/${employee._id}/documents/${confirmDoc._id}/file/file2`,
                      confirmDoc.file2_content_type,
                      "aláírt adatlap",
                    )
                  }
                  data-testid="download-signed-data_confirmation"
                >
                  <Download className="h-3.5 w-3.5" />
                  Aláírt letöltése
                </Button>
              )}
              {isAwaiting && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSigning(confirmDoc)}
                  className="border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:bg-amber-500/10"
                  data-testid="sign-data_confirmation"
                >
                  <Upload className="h-3.5 w-3.5" />
                  Aláírt feltöltése
                </Button>
              )}
            </div>
          </StepShell>
        );
      })()}

      {/* STEPS 3-5: Contracts */}
      {contractCards.map(({ def, hasTemplate, doc, status }, i) => {
        const Icon = def.icon;
        const accent = ACCENT[def.accent];
        const isSigned = status === "signed";
        const isAwaiting = status === "awaiting";
        const locked = !dataStepDone;
        return (
          <StepShell
            key={def.key}
            index={3 + i}
            title={def.title}
            subtitle={def.description}
            done={isSigned}
            locked={locked}
            accent={def.accent}
            icon={Icon}
            testId={`step-contract-${def.key}`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <StatusChip status={status} />
              {!hasTemplate && (
                <span className="rounded-full border border-dashed border-border bg-background/60 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                  Nincs sablon — tölts fel a „Sablonok kezelése” menüben.
                </span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {!doc && (
                <Button
                  size="sm"
                  disabled={locked || !hasTemplate || generating === def.key}
                  onClick={() => generate(def.key)}
                  className={accent.button}
                  data-testid={`generate-${def.key}`}
                >
                  {generating === def.key ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5" />
                  )}
                  Generálás
                </Button>
              )}
              {doc && doc.file1_url && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    openProtectedFile(
                      `/employees/${employee._id}/documents/${doc._id}/file/file1`,
                      doc.file1_content_type,
                      "generált szerződés",
                    )
                  }
                  data-testid={`download-generated-${def.key}`}
                >
                  <Download className="h-3.5 w-3.5" />
                  Generált letöltése
                </Button>
              )}
              {doc && status === "awaiting" && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={locked || !hasTemplate || generating === def.key}
                  onClick={() => generate(def.key)}
                  data-testid={`regenerate-${def.key}`}
                >
                  {generating === def.key ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RotateCcw className="h-3.5 w-3.5" />
                  )}
                  Újragenerálás
                </Button>
              )}
              {doc && doc.file2_url && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    openProtectedFile(
                      `/employees/${employee._id}/documents/${doc._id}/file/file2`,
                      doc.file2_content_type,
                      "aláírt szerződés",
                    )
                  }
                  data-testid={`download-signed-${def.key}`}
                >
                  <Download className="h-3.5 w-3.5" />
                  Aláírt letöltése
                </Button>
              )}
              {isAwaiting && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSigning(doc)}
                  className="border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:bg-amber-500/10"
                  data-testid={`sign-${def.key}`}
                >
                  <Upload className="h-3.5 w-3.5" />
                  Aláírt feltöltése
                </Button>
              )}
            </div>
          </StepShell>
        );
      })}

      {/* STEPS 5-9: ID Photos */}
      <section
        data-testid="step-id-photos"
        className={cn(
          "relative overflow-hidden rounded-2xl border border-border bg-[hsl(var(--sipx-elevated))] p-6",
          !dataStepDone && "opacity-60",
        )}
      >
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <StepNumber index={6} done={idCards.every((x) => x.hasPhoto)} />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                6–10. lépés
              </p>
              <h3 className="text-[15px] font-semibold tracking-tight">
                Igazolvány-fotók (5 db)
              </h3>
            </div>
          </div>
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {idCards.filter((x) => x.hasPhoto).length} / {idCards.length} feltöltve
          </span>
        </header>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {idCards.map(({ c, hasPhoto }) => (
            <IdCardTile
              key={c.type}
              title={c.title}
              hint={c.hint}
              employeeId={employee._id}
              docType={c.type}
              hasPhoto={hasPhoto}
              locked={!dataStepDone}
              onChanged={onChanged}
            />
          ))}
        </div>
      </section>

      {/* Templates manager */}
      <TemplatesManagerDialog
        open={tplOpen}
        onOpenChange={setTplOpen}
        templates={templates}
        onChanged={refreshTemplates}
      />

      {/* Signed upload dialog */}
      <SignedUploadDialog
        open={!!signing}
        onOpenChange={(v) => !v && setSigning(null)}
        doc={signing}
        employeeId={employee._id}
        onUploaded={() => {
          setSigning(null);
          onChanged?.();
        }}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* StepShell — uniform step container                                         */
/* -------------------------------------------------------------------------- */

function StepShell({
  index,
  title,
  subtitle,
  done,
  locked,
  accent = "slate",
  icon: Icon,
  children,
  testId,
}) {
  const a = ACCENT[accent] || ACCENT.slate;
  return (
    <section
      data-testid={testId}
      className={cn(
        "relative overflow-hidden rounded-2xl border bg-gradient-to-br p-6 transition-all",
        "border-border",
        a.grad,
        done ? "ring-1 ring-emerald-300/50" : "",
        locked && "opacity-60",
      )}
    >
      <div className="flex items-start gap-4">
        <StepNumber index={index} done={done} />
        <div className={cn("grid h-11 w-11 shrink-0 place-items-center rounded-xl", a.iconBg)}>
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-[15.5px] font-semibold tracking-tight leading-tight">
            {title}
          </h3>
          {subtitle && (
            <p className="mt-0.5 text-[12.5px] leading-relaxed text-muted-foreground">
              {subtitle}
            </p>
          )}
          <div className="mt-3">{children}</div>
        </div>
        {done && (
          <CheckCircle2
            className="h-5 w-5 shrink-0 text-emerald-500"
            strokeWidth={2}
            aria-label="Kész"
          />
        )}
      </div>
    </section>
  );
}

function StepNumber({ index, done }) {
  return (
    <span
      className={cn(
        "grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-semibold tabular-nums transition-colors",
        done
          ? "bg-emerald-500 text-white"
          : "bg-foreground/[0.07] text-foreground/70",
      )}
    >
      {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : index}
    </span>
  );
}

function StatusChip({ status }) {
  if (status === "signed")
    return (
      <span className="inline-flex h-5 items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
        <CheckCircle2 className="h-3 w-3" /> Aláírva
      </span>
    );
  if (status === "awaiting")
    return (
      <span className="inline-flex h-5 items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 text-[10px] font-semibold uppercase tracking-wider text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
        <AlertTriangle className="h-3 w-3" /> Aláírásra vár
      </span>
    );
  return (
    <span className="inline-flex h-5 items-center gap-1 rounded-full border border-border bg-background/60 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      <Circle className="h-3 w-3" /> Még nincs
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* IdCardTile                                                                 */
/* -------------------------------------------------------------------------- */

function IdCardTile({ title, hint, employeeId, docType, hasPhoto, locked, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const inputId = `id-photo-${docType}`;

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      fd.append("file1", f);
      await api.post(`/employees/${employeeId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${title} elmentve.`);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      data-testid={`id-card-${docType}`}
      className={cn(
        "group relative flex aspect-[5/3.4] flex-col overflow-hidden rounded-xl border bg-gradient-to-br from-background to-background/70 p-3 transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-18px_rgba(15,23,42,0.3)]",
        hasPhoto
          ? "border-emerald-300/70 from-emerald-50/40 to-emerald-50/20 dark:from-emerald-500/10 dark:to-emerald-500/[0.04]"
          : "border-dashed border-border",
      )}
    >
      <div className="flex items-start justify-between">
        <div
          className={cn(
            "grid h-7 w-7 place-items-center rounded-md transition-colors",
            hasPhoto
              ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
              : "bg-muted text-muted-foreground",
          )}
        >
          <IdCard className="h-3.5 w-3.5" strokeWidth={1.75} />
        </div>
        {hasPhoto ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-500" strokeWidth={2} />
        ) : (
          <span className="rounded-full border border-dashed border-border bg-background/60 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
            Hiányzik
          </span>
        )}
      </div>
      <div className="mt-auto">
        <p className="text-[12.5px] font-semibold leading-tight">{title}</p>
        <p className="mt-0.5 line-clamp-1 text-[10.5px] text-muted-foreground">{hint}</p>
        <input
          id={inputId}
          type="file"
          accept=".pdf,application/pdf,image/jpeg,image/png,image/webp,image/heic"
          className="hidden"
          onChange={onPick}
          data-testid={`id-card-input-${docType}`}
        />
        <Button
          type="button"
          size="sm"
          variant={hasPhoto ? "outline" : "default"}
          className="mt-2 h-7 w-full text-[11px]"
          disabled={uploading || locked}
          onClick={() => document.getElementById(inputId)?.click()}
          data-testid={`id-card-upload-${docType}`}
        >
          {uploading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <>
              <Camera className="h-3 w-3" />
              {hasPhoto ? "Csere" : "Befotózás"}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* TemplatesManagerDialog                                                     */
/* -------------------------------------------------------------------------- */

function TemplatesManagerDialog({ open, onOpenChange, templates, onChanged }) {
  const [uploadingKind, setUploadingKind] = useState("");
  const [variables, setVariables] = useState(null);
  const [showVars, setShowVars] = useState(false);

  useEffect(() => {
    if (!open || variables) return;
    api
      .get("/contract-templates/variables")
      .then(({ data }) => setVariables(data))
      .catch(() => {});
  }, [open, variables]);

  const onUpload = async (kind, file) => {
    setUploadingKind(kind);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      await api.post("/contract-templates", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Sablon feltöltve.");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Feltöltés sikertelen.");
    } finally {
      setUploadingKind("");
    }
  };

  const onDelete = async (tpl) => {
    if (!window.confirm("Törlöd ezt a sablont?")) return;
    try {
      await api.delete(`/contract-templates/${tpl._id}`);
      toast.success("Sablon törölve.");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Törlés sikertelen.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="templates-manager">
        <DialogHeader>
          <DialogTitle>Szerződés sablonok</DialogTitle>
          <DialogDescription>
            Slot-onként egy aktív sablon. Új feltöltés automatikusan inaktívvá
            teszi a korábbit az adott slotban.
            <br />
            <span className="text-foreground">
              Word (.docx) sablon esetén
            </span>{" "}
            használj <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">{`{{ valtozo }}`}</code>{" "}
            formátumú helyettesítőket — generáláskor a dolgozó adataival
            töltjük ki őket.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {templates.map(({ kind, label, template }) => {
            const inputId = `tpl-upload-${kind}`;
            return (
              <div
                key={kind}
                className="flex items-center gap-3 rounded-lg border border-border bg-muted/20 px-3 py-2.5"
                data-testid={`template-slot-${kind}`}
              >
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-foreground/[0.06] text-muted-foreground">
                  <ScrollText className="h-3.5 w-3.5" strokeWidth={1.75} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] font-medium leading-tight">{label}</p>
                  {template ? (
                    <p className="truncate text-[11px] text-muted-foreground">
                      <span className="font-mono">{template.file_name}</span>
                      {" · "}
                      {template.created_by_name}
                    </p>
                  ) : (
                    <p className="text-[11px] text-muted-foreground/70">
                      Nincs feltöltött sablon.
                    </p>
                  )}
                </div>
                <input
                  id={inputId}
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,application/pdf,image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) onUpload(kind, f);
                  }}
                  data-testid={`template-input-${kind}`}
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8"
                  disabled={uploadingKind === kind}
                  onClick={() => document.getElementById(inputId)?.click()}
                  data-testid={`template-upload-${kind}`}
                >
                  {uploadingKind === kind ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <>
                      <Upload className="h-3 w-3" />
                      {template ? "Csere" : "Feltöltés"}
                    </>
                  )}
                </Button>
                {template && (
                  <button
                    type="button"
                    aria-label="Sablon törlése"
                    className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => onDelete(template)}
                    data-testid={`template-delete-${kind}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Használható változók referencia */}
        <div className="rounded-lg border border-border bg-muted/10 px-3 py-2.5">
          <button
            type="button"
            onClick={() => setShowVars((v) => !v)}
            className="flex w-full items-center justify-between text-left text-[12.5px] font-medium"
            data-testid="toggle-template-variables"
          >
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-amber-500" />
              Használható változók (.docx sablonokhoz)
            </span>
            <span className="text-[11px] text-muted-foreground">
              {showVars ? "Elrejt" : "Mutat"}
            </span>
          </button>
          {showVars && variables?.groups && (
            <div className="mt-2.5 space-y-2.5">
              <p className="text-[11px] text-muted-foreground">
                Szintaxis: <code className="rounded bg-background px-1 py-0.5 font-mono">{`{{ valtozo }}`}</code>
                . Kattints a változóra a vágólapra másoláshoz.
              </p>
              {variables.groups.map((g) => (
                <div key={g.name}>
                  <p className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                    {g.name}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {g.variables.map((v) => {
                      const tag = `{{ ${v.key} }}`;
                      return (
                        <button
                          type="button"
                          key={v.key}
                          title={`${v.label} — pl.: ${v.example}`}
                          onClick={() => {
                            try {
                              navigator.clipboard?.writeText(tag);
                              toast.success(`Másolva: ${tag}`);
                            } catch {
                              /* clipboard nem elérhető */
                            }
                          }}
                          className="group inline-flex items-center gap-1 rounded-md border border-border bg-background px-1.5 py-0.5 font-mono text-[10.5px] hover:border-foreground/40"
                          data-testid={`tpl-var-${v.key}`}
                        >
                          <span>{tag}</span>
                          <span className="text-muted-foreground/70 group-hover:text-foreground">
                            {v.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Bezárás
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/* SignedUploadDialog                                                         */
/* -------------------------------------------------------------------------- */

function SignedUploadDialog({ open, onOpenChange, doc, employeeId, onUploaded }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) setFile(null);
  }, [open]);

  if (!doc) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Válassz ki egy aláírt PDF/képet a feltöltéshez.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(
        `/employees/${employeeId}/documents/${doc._id}/sign`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("Aláírt példány feltöltve.");
      onUploaded?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Feltöltés sikertelen.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="signed-upload-dialog">
        <DialogHeader>
          <DialogTitle>Aláírt példány feltöltése</DialogTitle>
          <DialogDescription>
            Tölts fel egy aláírt szkennelt változatot (PDF / JPG / PNG).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <input
            type="file"
            accept=".pdf,application/pdf,image/jpeg,image/png,image/webp,image/heic"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-foreground file:px-3 file:py-1.5 file:text-background hover:file:bg-foreground/90"
            data-testid="signed-file-input"
          />
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Mégse
            </Button>
            <Button type="submit" disabled={uploading || !file} data-testid="signed-submit">
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Feltöltés
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
